import React from "react";

/**
 * Tiny inline SVG sparkline. Renders a smooth line with a subtle area fill.
 * Values are drawn left-to-right (oldest → newest).
 */
export function Sparkline({
  values = [],
  width = 64,
  height = 20,
  color = "#DC1F26",
  strokeWidth = 1.5,
  showDot = true,
}) {
  const clean = values.filter((v) => typeof v === "number");
  if (clean.length < 2) {
    return (
      <svg width={width} height={height} data-testid="sparkline-empty" aria-hidden="true">
        <line
          x1="0"
          y1={height / 2}
          x2={width}
          y2={height / 2}
          stroke={color}
          strokeOpacity="0.25"
          strokeWidth={strokeWidth}
          strokeDasharray="2 2"
        />
      </svg>
    );
  }

  const max = Math.max(...clean, 1);
  const min = Math.min(...clean, 0);
  const range = max - min || 1;
  const stepX = clean.length > 1 ? width / (clean.length - 1) : width;

  const points = clean.map((v, i) => {
    const x = i * stepX;
    // Leave 2px padding so stroke doesn't clip
    const y = height - 2 - ((v - min) / range) * (height - 4);
    return [x, y];
  });

  const linePath = points
    .map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`)
    .join(" ");

  const areaPath = `${linePath} L${width},${height} L0,${height} Z`;

  const last = points[points.length - 1];
  const first = clean[0];
  const lastVal = clean[clean.length - 1];
  const trendUp = lastVal > first;

  return (
    <svg width={width} height={height} data-testid="sparkline" aria-hidden="true">
      <path d={areaPath} fill={color} fillOpacity="0.12" />
      <path d={linePath} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
      {showDot && (
        <circle
          cx={last[0]}
          cy={last[1]}
          r={2.2}
          fill={trendUp ? "#EF4444" : "#10B981"}
        />
      )}
    </svg>
  );
}
